import { Button, Form, InputNumber, Modal, Switch, Typography, message } from 'antd'
import { FC, useEffect } from 'react'
import { useUpdateGeneralStock } from 'hooks/useGeneralStock'
import type { ProductInventoryItem } from 'services/interface'
import { createInputNumberGuards } from 'utils'
import { formatGeneralStockDisplayName } from '../inventoryHelpers'

interface GeneralStockSafeStockModalProps {
  open: boolean
  item: ProductInventoryItem | null
  onClose: () => void
}

const GeneralStockSafeStockModal: FC<GeneralStockSafeStockModalProps> = ({
  open,
  item,
  onClose,
}) => {
  const [form] = Form.useForm<{ is_active: boolean; min_quantity: number }>()
  const { mutate: updateMutate, isPending: saving } = useUpdateGeneralStock()
  // จำนวนสต็อกรับได้เฉพาะจำนวนเต็ม
  const integerGuards = createInputNumberGuards({ mode: 'integer' })

  useEffect(() => {
    if (!open || !item) return
    form.setFieldsValue({
      is_active: item.stock?.is_safe_stock_enabled ?? false,
      min_quantity: item.stock?.safe_stock_quantity ?? 0,
    })
  }, [open, item, form])

  const submit = () => {
    if (!item?.id) return
    form.validateFields().then((values) => {
      updateMutate(
        {
          id: item.id,
          safe_stock_quantity: values.min_quantity,
          is_safe_stock_enabled: values.is_active,
        },
        {
          onSuccess: () => {
            message.success('บันทึกการแจ้งเตือนสต็อกแล้ว')
            onClose()
          },
          onError: (err) => message.error(err.message || 'บันทึกไม่สำเร็จ'),
        }
      )
    })
  }

  const clearAlert = () => {
    if (!item?.id) return
    updateMutate(
      {
        id: item.id,
        safe_stock_quantity: 0,
        is_safe_stock_enabled: false,
      },
      {
        onSuccess: () => {
          message.success('ปิดการแจ้งเตือนสต็อกแล้ว')
          onClose()
        },
        onError: (err) => message.error(err.message || 'ดำเนินการไม่สำเร็จ'),
      }
    )
  }

  const displayName = item
    ? formatGeneralStockDisplayName(item.name, item.description)
    : ''

  return (
    <Modal
      title={
        item ? (
          <span>
            แจ้งเตือนสต็อก — <Typography.Text strong>{displayName}</Typography.Text>
          </span>
        ) : (
          'แจ้งเตือนสต็อก'
        )
      }
      open={open}
      maskClosable={!saving}
      onCancel={() => {
        if (saving) return
        onClose()
      }}
      destroyOnClose
      footer={[
        <Button key="clear" danger loading={saving} onClick={() => void clearAlert()} disabled={!item}>
          ล้าง / ปิดการแจ้งเตือน
        </Button>,
        <Button key="cancel" disabled={saving} onClick={onClose}>
          ยกเลิก
        </Button>,
        <Button key="ok" type="primary" loading={saving} onClick={() => void submit()}>
          บันทึก
        </Button>,
      ]}
      width={440}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
        เมื่อเปิดใช้ ระบบจะถือว่าควรแจ้งเตือนเมื่อจำนวน<strong>พร้อมขาย</strong>น้อยกว่าหรือเท่ากับเกณฑ์ที่กำหนด
      </Typography.Paragraph>
      <Form
        disabled={saving}
        form={form}
        layout="vertical"
        initialValues={{ is_active: false, min_quantity: 0 }}
      >
        <Form.Item label="เปิดการแจ้งเตือน" name="is_active" valuePropName="checked">
          <Switch checkedChildren="เปิด" unCheckedChildren="ปิด" />
        </Form.Item>
        <Form.Item
          label="เกณฑ์ (ชิ้น) — แจ้งเมื่อพร้อมขาย ≤ ค่านี้"
          name="min_quantity"
          rules={[
            { required: true, message: 'กรุณาระบุจำนวน' },
            {
              validator: (_, v) =>
                v >= 0 ? Promise.resolve() : Promise.reject(new Error('ต้องไม่ติดลบ')),
            },
          ]}
        >
          <InputNumber
            min={0}
            precision={0}
            step={1}
            style={{ width: '100%' }}
            placeholder="เช่น 10"
            {...integerGuards}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default GeneralStockSafeStockModal
